import { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import StipulatorContext from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import { HpmsCustomCardApi } from '@philips-emr/hpms-stipulator-components/lib/api';
import { HPMSOnCreateMenu, HPMSOnOptionClick } from '@philips-emr/hpms-stipulator-components/lib/events/event';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent } from '@philips-emr/hpms-stipulator-components/lib/types';
import { FetchAPI, PanelAPI } from '@philips-emr/tws-components';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnBeforeFetch, OnClose, OnDynamicContentRender, OnMount } from '@philips-emr/tws-core/lib/client';
import { MountEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';

import BaseController from './BaseController';
import { getLoteStampBySituacao, LoteSituacao } from './lote/lote-utils';

enum SituacaoVinculo {
  APROVADO = 'A',
  REJEITADO = 'R',
  PENDENTE = 'P',
}

interface Data {
  dsStatus: string;
  ieStatus: SituacaoVinculo;
}

const OptionKey = {
  OBSERVACAO: 'observacao',
};

interface ObsData {
  nrSeqBeneficiario: number;
  nrSeqProtocolo: number;
  ieStatus: string;
}

@Controller({ fragment: 'consulta-alteracao-cadastral' })
class ConsultaAlteracaoCadastral extends BaseController {

  @Inject('panel-modal-silicitacao')
  private panelModalSolicitacao: PanelAPI;

  @Inject('fetch-consulta')
  private fetchModalSolicitacao: FetchAPI;

  @OnMount({ target: ['panel-alteracao-cadastral'] })
  onMountPanelAlteracao(): void {
    const { cdPessoaFisica, nrSeqBeneficiario } = this.historyService.getCurrentRouteParams();
    this.context.set({ cdPessoaFisica, nrSeqBeneficiario });
  }

  @OnMount({ target: ['hpms-custom-card-alteracao-cadastral'] })
  onHpmsCustomCard(event: MountEvent): void {
    const api = event.target as unknown as HpmsCustomCardApi;
    api.setShowMenu(false);
  }

  @HPMSOnCreateMenu('hpms-custom-card-alteracao-cadastral')
  onCreateCardMenu(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, shouldRender } = event;
    const { ieStatus } = data as unknown as ObsData;

    if (ieStatus != SituacaoVinculo.REJEITADO) {
      shouldRender(false);
    }
  }

  @OnDynamicContentRender({ target: 'hpms-custom-card-alteracao-cadastral' })
  onDynamicContentRenderCardLote(event: RenderEvent): void {
    const { data, render } = event;
    const { dsStatus, ieStatus } = data as Data;

    const situacaoMap = {
      [SituacaoVinculo.APROVADO]: LoteSituacao.INTEGRADO_A_PROPOSTA,
      [SituacaoVinculo.PENDENTE]: LoteSituacao.AGUARDANDO_ACAO_DO_USUARIO,
      [SituacaoVinculo.REJEITADO]: LoteSituacao.REJEITADA_PELA_OPERADORA,
    };

    const content = (): ReactElement => (getLoteStampBySituacao(situacaoMap[ieStatus], dsStatus));

    render(content);
  }

  @OnClose({ target: 'modal-data-change-details' })
  onCloseModal(): void {
    this.panelModalSolicitacao.setVisible(false);
  }

  @OnBeforeFetch({ target: 'fetch-consulta' })
  onBeforeFetchBeneficiarios(event: MountEvent): void {
    const fetch = event.target as unknown as FetchAPI;
    const { nrSeqSubestipulante } = StipulatorContext.getCurrentContract();
    nrSeqSubestipulante && fetch.setParameters({ ...fetch.getParameters(), nrSeqSubestipulante });
  }

  @HPMSOnOptionClick('hpms-custom-card-alteracao-cadastral')
  onOptionClick(event: HpmsCustomCardEvent<Record<string, Value>>): void {
    const { data, key } = event;
    const { nrSeqProtocolo } = data as unknown as ObsData;
    const { nrSeqBeneficiario } = this.historyService.getCurrentRouteParams() as unknown as ObsData;

    this.context.set({ nrSeqProtocolo });

    const action = {
      [OptionKey.OBSERVACAO]: () => this.observacao(nrSeqProtocolo, nrSeqBeneficiario),
    };

    if (action[key]) {
      action[key]();
    }
  }

  @OnMount('fetch-consulta')
  onMountFetchModal(): void {
    const { nrSeqSolicAlt, nrSeqBeneficiario } = this.historyService.getCurrentRouteParams();

    this.fetchModalSolicitacao.setParameters({ nrSeqBeneficiario, ...(nrSeqSolicAlt && { nrSeqSolicAlt }) });
    this.fetchModalSolicitacao.forceFetch();
  }

  private observacao = (nrSeqProtocolo: number, nrSeqBeneficiario: number): void => {
    this.panelModalSolicitacao.setVisible(true);
    this.context.set({ nrSeqProtocolo, nrSeqBeneficiario });
  };
}

export default ConsultaAlteracaoCadastral;
